import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import { useTable, useSortBy, usePagination, useFilters, useRowSelect } from "react-table";
import FilterModal from "../FilterModal/FilterModal";
import "./SegmAnnoLabelList.css";

const SegmAnnoLabelList = ({ refreshData, onSelectedIdsChange }) => {
  const location = useLocation();
  const token = localStorage.getItem("token");
  const [labels, setLabels] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [placeholder, setPlaceholder] = useState("");

  const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
      const defaultRef = React.useRef();
      const resolvedRef = ref || defaultRef;

      useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
      }, [resolvedRef, indeterminate]);

      return <input type="checkbox" ref={resolvedRef} {...rest} />;
    }
  );

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }) => (
          <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
        ),
        Cell: ({ row }) => (
          <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
        ),
      },
      {
        Header: "ID",
        accessor: "id",
        filterType: "Exact",
      },
      {
        Header: "Foreign Key",
        accessor: "foreign_key",
        Cell: ({ value }) => (
          <div>
            <div>
              <strong>Modality:</strong>
              {value.modality === "XA" && (
              <Link style={{ textDecoration: "none" }} to={`/dicoms`}>
                {value.modality.toUpperCase()}
              </Link>
              )}
            </div>
            <div>
              <strong>ID:</strong>
              <Link style={{ textDecoration: "none" }} to={`/${value.modality}s/${value.id}`}>
                {value.id}
              </Link>
            </div>
          </div>
        ),
      },
      {
        Header: "File Path",
        accessor: "file_path",
      },
      {
        Header: "Frame Index",
        accessor: "frame_index",
      },
      {
        Header: "Datasets",
        accessor: "datasets",
        Cell: ({ value }) => (Array.isArray(value) ? value.join(", ") : "N/A"),
      },
      {
        Header: "Annotator",
        accessor: "annotator",
      },
      {
        Header: "Cardiac Phase",
        accessor: "cardiac_phase",
      },
      {
        Header: "Creation Time",
        accessor: "creation_time",
        Cell: ({ value }) => new Date(value).toLocaleString(),
        filterType: "Exact",
      },
      {
        Header: "Updated Time",
        accessor: "updated_time",
        Cell: ({ value }) => new Date(value).toLocaleString(),
        filterType: "Exact",
      },
      {
        Header: "Uploader",
        accessor: "uploader",
        filterType: "Exact",
      },
      {
        Header: "Actions",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => {
          const label = row.original;
          return (
            <div className="actions-container">
              <Link style={{ textDecoration: "none" }} to={`/segm_anno_labels/${label.id}`}>
                <button className="action-btn view">
                  <i className="fas fa-eye"></i> View
                </button>
              </Link>
              <Link style={{ textDecoration: "none" }} to={`/segm_anno_labels/${label.id}/edit`}>
                <button className="action-btn edit">
                  <i className="fas fa-edit"></i> Edit
                </button>
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({}), []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    state,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data: labels,
      defaultColumn,
      manualPagination: true,
      manualSortBy: true,
      manualFilters: true,
      pageCount,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const { pageIndex, pageSize, sortBy, filters } = state;

  useEffect(() => {
    onSelectedIdsChange(selectedFlatRows.map(row => row.original.id));
  }, [selectedFlatRows, onSelectedIdsChange]);

  const fetchLabels = useCallback(async () => {
    setLoading(true);
    const searchParams = new URLSearchParams(location.search);
    const dicomId = searchParams.get("dicom_id");

    let endpoint = `${process.env.REACT_APP_BACKEND_URL}/patients/segm_anno_labels/`;

    if (dicomId) {
      endpoint += `?dicom_id=${dicomId}`;
    }

    try {
      const { pageIndex, pageSize, sortBy, filters } = state;
      let params = {
        page: pageIndex + 1,
        page_size: pageSize,
      };

      if (sortBy?.length > 0) {
        const { id, desc } = sortBy[0];
        params.sort_by = id;
        params.sort_dir = desc ? "desc" : "asc";
      }

      if (filters?.length) {
        filters.forEach(({ id, value }) => {
          if (value) {
            params[id] = value;
          }
        });
      }

      const res = await axios.get(endpoint, {
        params,
        headers: { Authorization: `Token ${token}` },
      });
      const { results, pageCount: serverPageCount } = res.data;
      setLabels(results || []);
      setPageCount(serverPageCount || 0);

      if (!results || !results.length) {
        setPlaceholder("No SegmAnno labels found.");
      } else {
        setPlaceholder("");
      }
    } catch (error) {
      console.error(error);
      setPlaceholder("Error fetching SegmAnno labels.");
    }
    setLoading(false);
  }, [pageIndex, pageSize, sortBy, filters, token, location.search]);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels, refreshData]);

  if (loading) return <div>Loading SegmAnno Labels...</div>;
  if (!page.length && placeholder) {
    return (
      <div className="placeholder-message">
        {placeholder}{" "}
        {filters.length > 0 && (
          <button className="filter-button" onClick={() => setAllFilters([])}>
            Clear Filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="segm-anno-label-list-container">
      <div className="table-actions">
        <button className="filter-button" onClick={() => setFilterModalOpen(true)}>
          Filter Data
        </button>
      </div>
      <div className="table-scrollable">
        <table {...getTableProps()} className="segm-anno-label-list-table">
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={headerGroupKey} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: colKey, ...colProps } = column.getHeaderProps();
                  return (
                    <th key={colKey} {...colProps}>
                      <div {...(column.getSortByToggleProps ? column.getSortByToggleProps() : {})}>
                        {column.render("Header")}
                        {column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""}
                      </div>
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const { key: rowKey, ...rowProps } = row.getRowProps();
            return (
              <tr key={rowKey} {...rowProps}>
                {row.cells.map((cell) => {
                  const { key: cellKey, ...cellProps } = cell.getCellProps();
                  return (
                    <td key={cellKey} {...cellProps} data-column={cell.column.id}>
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          Previous
        </button>
        <span>
          Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
        </span>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          Next
        </button>
        <select style={{ width: "auto" }} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
          {[10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        columns={columns.filter((col) => !col.disableFilters)}
        currentFilters={state.filters}
        onApplyFilters={(filters) => {
          setAllFilters(filters);
        }}
      />
    </div>
  );
};

export default SegmAnnoLabelList;
